/**
 * ------------------------------------------------------------
 * Developer Name : Praveen Tripathi
 * Purpose        : UnivoCarousel presets (home + landing)
 * ------------------------------------------------------------
 *
 * Purpose: Named carousel settings so pages don't reinvent config/CSS.
 * Performance Notes: Stable preset objects → fewer child re-renders / better INP.
 */

import type { UnivoCarouselPresetName, UnivoCarouselSettings } from "./types";

export const UNIVO_CAROUSEL_PRESETS: Record<
  UnivoCarouselPresetName,
  UnivoCarouselSettings
> = {
  reviews: {
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 300,
    autoplay: { status: true, delay: 3000 },
    pagination: { status: false },
    breakpoints: {
      320: { slidesPerView: 1, spaceBetween: 5 },
      640: { slidesPerView: 2.2, spaceBetween: 5 },
      710: { slidesPerView: 2.5, spaceBetween: 5 },
      805: { slidesPerView: 3, spaceBetween: 10 },
      1050: { slidesPerView: 3, spaceBetween: 5 },
    },
  },
  accreditation: {
    slidesPerView: 3,
    spaceBetween: 20,
    autoplay: { status: true, delay: 3000 },
    navigation: { status: false },
    pagination: { status: false, clickable: true },
    breakpoints: {
      320: { slidesPerView: 1.2, spaceBetween: 10 },
      360: { slidesPerView: 2, spaceBetween: 5 },
      768: { slidesPerView: 2.1, spaceBetween: 10 },
      1024: { slidesPerView: 4, spaceBetween: 10 },
      1200: { slidesPerView: 5, spaceBetween: 10 },
    },
  },
  whyAmity: {
    slidesPerView: 3,
    spaceBetween: 0,
    autoplay: { status: true, delay: 3000 },
    navigation: { status: false },
    pagination: { status: false, clickable: true },
    breakpoints: {
      320: { slidesPerView: 2, spaceBetween: -1 },
      768: { slidesPerView: 2, spaceBetween: 5 },
      1024: { slidesPerView: 4, spaceBetween: 10 },
    },
  },
  blogs: {
    slidesPerView: 2,
    spaceBetween: 12,
    autoplay: { status: false },
    pagination: { status: false },
    breakpoints: {
      0: { slidesPerView: 2, spaceBetween: 12 },
      520: { slidesPerView: 2, spaceBetween: 12 },
      1024: { slidesPerView: 4 },
      1440: { slidesPerView: 4, spaceBetween: 24 },
    },
  },
  programs: {
    slidesPerView: 1,
    spaceBetween: 5,
    speed: 500,
    autoplay: { status: false, delay: 6000 },
    pagination: { status: false },
    pageType: "program",
    breakpoints: {
      320: { slidesPerView: 1.1, spaceBetween: 5 },
      400: { slidesPerView: 1.4, spaceBetween: 5 },
      500: { slidesPerView: 1.7, spaceBetween: 5 },
      900: { slidesPerView: 3, spaceBetween: 5 },
      1201: { slidesPerView: 4, spaceBetween: 5 },
      1440: { slidesPerView: 4, spaceBetween: 5 },
      3000: { slidesPerView: 5, spaceBetween: 5 },
    },
  },
  statsMobile: {
    slidesPerView: 2,
    spaceBetween: 0,
    speed: 400,
    autoplay: { status: true, delay: 2500 },
    pagination: { status: false },
  },
  midBannerMobile: {
    slidesPerView: 2,
    spaceBetween: 16,
    autoplay: { status: true, delay: 3000 },
    pagination: { status: false },
    breakpoints: {
      320: { slidesPerView: 1.1 },
      450: { slidesPerView: 1.1, spaceBetween: 5 },
      500: { slidesPerView: 2, spaceBetween: 5 },
      870: { slidesPerView: 3, spaceBetween: 5 },
      1024: { slidesPerView: 4, spaceBetween: 5 },
    },
  },
  heroPrograms: {
    slidesPerView: 4,
    spaceBetween: 5,
    speed: 800,
    autoplay: { status: true, delay: 3000 },
    pagination: { status: false },
    breakpoints: {
      320: { slidesPerView: 2 },
      350: { slidesPerView: 2, spaceBetween: 5 },
      450: { slidesPerView: 3, spaceBetween: 5 },
      500: { slidesPerView: 3, spaceBetween: 5 },
      768: { slidesPerView: 4, spaceBetween: 5 },
      820: { slidesPerView: 4, spaceBetween: 5 },
    },
  },
  beyondScreenMobile: {
    slidesPerView: 2,
    spaceBetween: 12,
    autoplay: { status: false },
    pagination: { status: false },
    breakpoints: {
      0: { slidesPerView: 2, spaceBetween: 12 },
      520: { slidesPerView: 2, spaceBetween: 12 },
      1024: { slidesPerView: 3, spaceBetween: 24 },
    },
  },
  beyondScreenOuter: {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 400,
    autoplay: { status: false },
    pagination: { status: false },
    navigation: { status: false },
  },
  beyondScreenInner: {
    slidesPerView: 5,
    spaceBetween: 2,
    speed: 400,
    autoplay: { status: true, delay: 1500 },
    pagination: { status: false },
    navigation: { status: false },
  },
  landingBanner: {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 500,
    autoplay: { status: true, delay: 4000 },
    pagination: { status: true, clickable: true },
    navigation: { status: false },
  },
  landingCampus: {
    slidesPerView: 1.2,
    spaceBetween: 12,
    speed: 400,
    autoplay: { status: true, delay: 3000 },
    pagination: { status: false },
    breakpoints: {
      320: { slidesPerView: 1.1, spaceBetween: 8 },
      768: { slidesPerView: 2.2, spaceBetween: 12 },
      1024: { slidesPerView: 3, spaceBetween: 16 },
    },
  },
};

export function mergeCarouselSettings(
  preset?: UnivoCarouselPresetName,
  settings?: UnivoCarouselSettings
): UnivoCarouselSettings {
  const base = preset ? UNIVO_CAROUSEL_PRESETS[preset] : {};
  if (!settings) return { ...base };

  return {
    ...base,
    ...settings,
    autoplay: {
      ...base.autoplay,
      ...settings.autoplay,
    },
    pagination: {
      ...base.pagination,
      ...settings.pagination,
    },
    navigation: {
      ...base.navigation,
      ...settings.navigation,
    },
    breakpoints: settings.breakpoints ?? base.breakpoints,
  };
}
